'use client';

import { ArrowRight, Loader2, RotateCw, X } from 'lucide-react';

type Status = 'queued' | 'processing' | 'completed' | 'failed';

interface Props {
  status: Status;
  progress: number;
  videoId: string | null;
  onCancel: () => void;
  onRetry: () => void;
  t: (key: string) => string;
  errorMessage?: string | null;
}

export function RenderProgressCard({ status, progress, videoId, onCancel, onRetry, t, errorMessage }: Props) {
  const labelKey =
    status === 'queued'
      ? 'studio.editor.renderQueued'
      : status === 'processing'
        ? 'studio.editor.renderProcessing'
        : status === 'completed'
          ? 'studio.editor.renderCompleted'
          : 'studio.editor.renderFailed';

  return (
    <div className="mt-3 rounded-xl border border-white/10 bg-[#15091f]/90 p-3 shadow-[0_20px_60px_rgba(0,0,0,0.45)] backdrop-blur-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-black uppercase tracking-[0.16em] text-brand-300">{t(labelKey)}</span>
        <span className="text-[10px] font-bold tabular-nums text-white/50">{progress}%</span>
      </div>

      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10">
        <div
          className="h-full rounded-full bg-brand-500 transition-[width] duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>

      {errorMessage && status === 'failed' && (
        <p className="mt-2 text-[11px] font-medium text-white/50">{errorMessage}</p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {status === 'processing' && (
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex h-7 items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2.5 text-[11px] font-bold text-white/70 transition hover:border-red-400/40 hover:text-red-400"
          >
            <X className="h-3 w-3" />
            {t('studio.editor.renderCancel')}
          </button>
        )}
        {status === 'queued' && (
          <span className="inline-flex h-7 items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2.5 text-[11px] font-bold text-white/50">
            <Loader2 className="h-3 w-3 animate-spin" />
            {t('studio.editor.renderQueued')}
          </span>
        )}
        {status === 'failed' && (
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex h-7 items-center gap-1 rounded-full bg-white px-2.5 text-[11px] font-black text-ink-950 shadow-sm transition hover:bg-white/90"
          >
            <RotateCw className="h-3 w-3" />
            {t('studio.editor.renderRetry')}
          </button>
        )}
        {status === 'completed' && (
          <span className="inline-flex h-7 items-center gap-1 rounded-full bg-brand-600 px-2.5 text-[11px] font-black text-white shadow-[0_16px_34px_rgba(91,53,255,0.35)]">
            {t('studio.editor.openVideo')}
            <ArrowRight className="h-3 w-3" />
          </span>
        )}
      </div>
    </div>
  );
}
